<template>
  <div class="pa-4">
    <div class="d-flex align-center mb-4">
      <v-btn
        icon
        :to="{ name: 'admin-cash-register-closings', params: { club_slug: $route.params.club_slug } }"
      >
        <v-icon>{{ mdiArrowLeft }}</v-icon>
      </v-btn>
      <h2 class="text-h6 ml-2">
        清算詳細
      </h2>
      <v-spacer />
      <v-btn
        v-if="closing && closing.status === 'finalized'"
        color="warning"
        outlined
        :loading="unfinalizing"
        @click="unfinalize"
      >
        取消して再編集
      </v-btn>
    </div>

    <v-alert
      v-if="errorMessage"
      type="error"
      dense
      class="mb-4"
      dismissible
      @input="errorMessage = ''"
    >
      {{ errorMessage }}
    </v-alert>

    <v-dialog
      v-model="confirmUnfinalizeDialog"
      max-width="480"
    >
      <v-card>
        <v-card-title>取消の確認</v-card-title>
        <v-card-text>
          この清算を取り消して下書きに戻しますか？<br>
          紐付く保管金等のレジ取引も一緒に削除されます。
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn
            text
            @click="confirmUnfinalizeDialog = false"
          >
            キャンセル
          </v-btn>
          <v-btn
            color="warning"
            :loading="unfinalizing"
            @click="executeUnfinalize"
          >
            取り消す
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-card v-if="closing">
      <v-card-text>
        <v-row>
          <v-col
            cols="12"
            md="3"
          >
            <div class="text--secondary">
              営業日
            </div>
            <div class="text-h6">
              {{ closing.businessDate }}
            </div>
          </v-col>
          <v-col
            cols="12"
            md="3"
          >
            <div class="text--secondary">
              ステータス
            </div>
            <v-chip
              :color="closing.status === 'finalized' ? 'success' : 'warning'"
              small
            >
              {{ closing.status === 'finalized' ? '確定済' : '下書き' }}
            </v-chip>
          </v-col>
          <v-col
            cols="12"
            md="3"
          >
            <div class="text--secondary">
              確定日時
            </div>
            <div>{{ closing.finalizedAt ? formatDateTime(closing.finalizedAt) : '-' }}</div>
          </v-col>
        </v-row>

        <v-divider class="my-4" />

        <v-row>
          <v-col
            cols="12"
            md="4"
          >
            <div class="text--secondary">
              金種カウント
            </div>
            <div class="text-h6">
              {{ formatYen(closing.countedCashAmount) }}
            </div>
          </v-col>
          <v-col
            cols="12"
            md="4"
          >
            <div class="text--secondary">
              帳簿額
            </div>
            <div class="text-h6">
              {{ formatYen(closing.expectedCashAmount) }}
            </div>
          </v-col>
          <v-col
            cols="12"
            md="4"
          >
            <div class="text--secondary">
              過不足金
            </div>
            <div
              class="text-h5"
              :class="diffClass(closing.diffAmount)"
            >
              {{ formatYen(closing.diffAmount) }}
            </div>
          </v-col>
          <v-col
            cols="12"
            md="6"
          >
            <div class="text--secondary">
              繰越準備金
            </div>
            <div class="text-body-1">
              {{ formatYen(closing.nextDayReserveAmount) }}
            </div>
          </v-col>
          <v-col
            cols="12"
            md="6"
          >
            <div class="text--secondary">
              保管金
            </div>
            <div class="text-body-1">
              {{ formatYen(closing.storageAmount) }}
            </div>
          </v-col>
        </v-row>

        <v-divider class="my-4" />

        <div class="text-subtitle-1 mb-2">
          金種カウント
        </div>
        <v-simple-table dense>
          <thead>
            <tr>
              <th>額面</th>
              <th class="text-right">
                枚数
              </th>
              <th class="text-right">
                小計
              </th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="d in closing.denominations"
              :key="d.denomination"
            >
              <td>¥{{ d.denomination.toLocaleString() }}</td>
              <td class="text-right">
                {{ d.count }}
              </td>
              <td class="text-right">
                {{ formatYen(d.denomination * d.count) }}
              </td>
            </tr>
          </tbody>
        </v-simple-table>

        <v-divider class="my-4" />

        <div class="text-subtitle-1 mb-2">
          差額の原因
        </div>
        <div
          v-if="!closing.diffReasons || closing.diffReasons.length === 0"
          class="text--secondary"
        >
          記録なし
        </div>
        <v-simple-table
          v-else
          dense
        >
          <thead>
            <tr>
              <th>カテゴリ</th>
              <th class="text-right">
                金額
              </th>
              <th>メモ</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="r in closing.diffReasons"
              :key="r.id"
            >
              <td>{{ categoryLabel(r.category) }}</td>
              <td class="text-right">
                {{ formatYen(r.amount) }}
              </td>
              <td>{{ r.note || '-' }}</td>
            </tr>
          </tbody>
        </v-simple-table>

        <v-divider
          v-if="closing.unresolvedReason || closing.memo"
          class="my-4"
        />

        <div
          v-if="closing.unresolvedReason"
          class="mb-3"
        >
          <div class="text--secondary">
            未解消理由
          </div>
          <div>{{ closing.unresolvedReason }}</div>
        </div>

        <div v-if="closing.memo">
          <div class="text--secondary">
            メモ
          </div>
          <div>{{ closing.memo }}</div>
        </div>
      </v-card-text>
    </v-card>
  </div>
</template>

<script>
import { ref, onMounted, getCurrentInstance } from '@vue/composition-api'
import { mdiArrowLeft } from '@mdi/js'
import CashRegisterClosingApi from '@/api/admin/CashRegisterClosing'

const CATEGORY_LABELS = {
  slip: '伝票',
  receipt: '領収書',
  expense_settlement: '経費清算',
  unauthorized_takeout: '不正持ち出し',
  other: 'その他',
}

export default {
  name: 'AdminCashRegisterClosingDetail',
  setup() {
    const vm = getCurrentInstance().proxy
    const closing = ref(null)

    const fetch = async () => {
      const res = await CashRegisterClosingApi.getClosing(vm.$route.params.id)
      const data = res.data?.cashRegisterClosing?.data

      if (data) closing.value = { id: data.id, ...data.attributes }
    }

    const unfinalizing = ref(false)

    const errorMessage = ref('')
    const confirmUnfinalizeDialog = ref(false)

    const unfinalize = () => {
      errorMessage.value = ''
      confirmUnfinalizeDialog.value = true
    }

    const executeUnfinalize = async () => {
      unfinalizing.value = true
      try {
        await CashRegisterClosingApi.unfinalizeClosing(vm.$route.params.id)
        confirmUnfinalizeDialog.value = false
        vm.$router.push({
          name: 'admin-cash-register-closing-new',
          params: { club_slug: vm.$route.params.club_slug },
          query: { id: vm.$route.params.id },
        })
      } catch (e) {
        errorMessage.value = e.response?.data?.error || '取消に失敗しました'
        confirmUnfinalizeDialog.value = false
      } finally {
        unfinalizing.value = false
      }
    }

    const formatYen = v => `¥${Number(v || 0).toLocaleString()}`
    const formatDateTime = v => new Date(v).toLocaleString('ja-JP')
    const diffClass = v => {
      if (v === 0) return ''

      return v > 0 ? 'success--text' : 'error--text'
    }
    const categoryLabel = v => CATEGORY_LABELS[v] || v

    onMounted(fetch)

    return {
      mdiArrowLeft,
      closing,
      unfinalizing,
      unfinalize,
      executeUnfinalize,
      confirmUnfinalizeDialog,
      errorMessage,
      formatYen,
      formatDateTime,
      diffClass,
      categoryLabel,
    }
  },
}
</script>
